import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";

interface AnsweredPage {
    /** The page the response answered: the API echoes the requested pageNumber. */
    pageNumber: number;
    totalPages: number;
}

// A shared or stale ?page beyond the last page is a dead end — Next disabled,
// Previous walking empty pages — so once a read has said how many pages there
// are, an out-of-range page is REPLACED (no history entry) with the last one.
// totalPages 0 is an empty result set: nothing to clamp to. Moved here from
// the category page so every paged customer grid clamps the same way (audit
// BR-12): category, search, vendor and browse home.
//
// Returns whether a clamp is pending: the answer in hand is beyond its own
// last page, so it has no items, and the clamped page's read is due (search
// and vendor wait out a 250 ms debounce; the others issue it one effect
// later). The caller ORs it into the grid's loading flag so that window shows
// the skeleton, not "No products found." (PR 395 review F1). It follows the
// answer alone, never `answered.pageNumber !== pageNumber`, which is also true
// across every debounced page or filter change and would flash the skeleton
// over results the page means to keep showing.
export const useClampPageToRange = (
    pageNumber: number,
    answered: AnsweredPage,
    isLoading: boolean,
): boolean => {
    const [searchParams, setSearchParams] = useSearchParams();
    const { pageNumber: answeredPageNumber, totalPages } = answered;

    useEffect(() => {
        // Only a response answered for THIS page may clamp it: on the commit a
        // page change lands, the state still holds the previous page's
        // envelope, and clamping against that would bounce the shopper
        // straight back.
        if (
            isLoading ||
            totalPages === 0 ||
            answeredPageNumber !== pageNumber ||
            pageNumber <= totalPages
        ) {
            return;
        }
        const next = new URLSearchParams(searchParams);
        if (totalPages <= 1) {
            next.delete("page");
        } else {
            next.set("page", String(totalPages));
        }
        setSearchParams(next, { replace: true });
    }, [
        answeredPageNumber,
        totalPages,
        isLoading,
        pageNumber,
        searchParams,
        setSearchParams,
    ]);

    return totalPages > 0 && answeredPageNumber > totalPages;
};
